import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { Minus, Plus, Trash2 } from "lucide-react";

const FREE_SHIP = 3500;

export default function Cart() {
  const { items, updateQty, removeItem, subtotal } = useCart();
  const remaining = Math.max(0, FREE_SHIP - subtotal);
  const pct = Math.min(100, (subtotal / FREE_SHIP) * 100);

  return (
    <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-16" data-testid="cart-page">
      <div className="ie-overline text-brand-accent mb-3">Your Bag</div>
      <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl tracking-tight mb-10 md:mb-12">Quietly considered.</h1>

      {items.length === 0 ? (
        <div className="py-24 text-center border border-border bg-surface" data-testid="cart-empty">
          <div className="ie-overline text-text-secondary mb-4">Your bag is empty</div>
          <h2 className="font-serif text-3xl mb-8">Begin a quiet edit.</h2>
          <Link to="/shop" className="ie-btn-primary inline-flex">Browse the Atelier</Link>
        </div>
      ) : (
        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-8">
            {/* Progress */}
            <div className="border-b border-border pb-6 mb-8" data-testid="cart-progress">
              {remaining > 0 ? (
                <div className="text-sm">
                  <span className="font-serif text-foreground">₹{remaining.toLocaleString("en-IN")}</span>
                  <span className="text-text-secondary"> away from complimentary shipping</span>
                </div>
              ) : (
                <div className="ie-overline text-brand-accent">Complimentary shipping unlocked</div>
              )}
              <div className="mt-3 h-px bg-border relative">
                <div className="absolute inset-y-0 left-0 bg-brand-accent" style={{ width: `${pct}%`, height: 1 }}/>
              </div>
            </div>

            <ul>
              {items.map((i) => (
                <li key={i.sku} className="flex gap-6 py-6 border-b border-border" data-testid={`cart-row-${i.sku}`}>
                  <Link to={`/product/${i.id}`} className="w-28 h-36 bg-surface flex-shrink-0 overflow-hidden">
                    <img src={i.image} alt={i.name} className="w-full h-full object-cover"/>
                  </Link>
                  <div className="flex-1">
                    <div className="flex justify-between gap-4">
                      <div>
                        <h3 className="font-serif text-xl">{i.name}</h3>
                        <div className="ie-overline text-text-secondary mt-1">Size {i.size}</div>
                      </div>
                      <button onClick={() => removeItem(i.sku)} className="text-text-secondary hover:text-foreground" aria-label="remove" data-testid={`cart-row-remove-${i.sku}`}>
                        <Trash2 className="w-4 h-4"/>
                      </button>
                    </div>
                    <div className="mt-6 flex items-center justify-between">
                      <div className="inline-flex items-center border border-border">
                        <button onClick={() => updateQty(i.sku, i.qty - 1)} className="px-3 py-2 hover:bg-muted" aria-label="decrease"><Minus className="w-3 h-3"/></button>
                        <span className="px-4 text-sm tabular-nums">{i.qty}</span>
                        <button onClick={() => updateQty(i.sku, i.qty + 1)} className="px-3 py-2 hover:bg-muted" aria-label="increase"><Plus className="w-3 h-3"/></button>
                      </div>
                      <span className="font-serif text-xl">₹{(i.price * i.qty).toLocaleString("en-IN")}</span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <aside className="md:col-span-4">
            <div className="bg-surface border border-border p-8 sticky top-28" data-testid="cart-summary">
              <div className="ie-overline mb-6">Order Summary</div>
              <div className="space-y-4 text-sm">
                <div className="flex justify-between">
                  <span className="text-text-secondary">Subtotal</span>
                  <span className="font-serif">₹{subtotal.toLocaleString("en-IN")}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-secondary">Shipping</span>
                  <span className="text-text-secondary">{remaining > 0 ? "Calculated at checkout" : "Free"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-secondary">Estimated tax</span>
                  <span className="text-text-secondary">Inclusive</span>
                </div>
                <div className="border-t border-border pt-4 flex justify-between items-baseline">
                  <span className="ie-overline">Total</span>
                  <span className="font-serif text-3xl">₹{subtotal.toLocaleString("en-IN")}</span>
                </div>
              </div>
              <Link to="/checkout" className="ie-btn-primary w-full justify-center mt-8" data-testid="cart-checkout-btn">
                Proceed to Checkout
              </Link>
              <p className="text-[11px] text-text-secondary mt-4 leading-relaxed">
                Each piece is inspected by hand before dispatch. Atelier returns within 30 days.
              </p>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
